import { STORE_NAME } from '@intigral/constant';
import { container, UserInfo } from '@intigral/domain';
import ResponseError from '@intigral/domain/lib/core/entities/errors/ResponseError';
import { get, set } from '@intigral/utils/lib/browser-storage/localStorage';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { IStore } from '../interfaces/StoreTypes';
import { loadProfileDone, profileInitialize, switchProfileMutationFn } from '../store/reducers/profileSlice';
import { fetchProfileList, getProfileListQueryKey } from './profileListQuery';
import { getSelectedProfileKidsMode, getUserInfo } from './user';
import { isGuestUser, isGuestBrowseProfile, onGuestUserSession, onSignedInUserSession } from '../guest';
import { log } from '@intigral/utils';
import { IProfileService } from '@intigral/domain/lib/application/services/ProfileService';
import { DI } from '@intigral/constant/config/DIContainer';

export {
    fetchProfileList,
    getProfileListFromCache,
    getProfileListQueryKey,
    refreshProfileListQuery,
} from './profileListQuery';

type ProfileValidationError = ResponseError | string;

export const useSelectedProfile = () => {
    const [profile] = useState(() => get(STORE_NAME.SELECTED_PROFILE));
    return profile;
};

export const useProfileList = (user: UserInfo | null) => {
    const kidsMode = getSelectedProfileKidsMode();
    return useQuery({
        queryKey: ['profiles', 'user-profile-list', kidsMode],
        enabled: user !== null,
        staleTime: Infinity,
        queryFn: async () => {
            if (isGuestUser(user)) {
                return {
                    parentalLockOn: 0,
                    profiles: [],
                    profilesLimit: 5,
                    profileRev: 0,
                    date: Date.now(),
                };
            }
            const profileService = container.resolve<IProfileService>(DI.SERVICES.IProfileService);
            const requestKidsMode = getSelectedProfileKidsMode();
            const profilesList: any = await profileService.getProfiles(
                user.apiKey,
                user.authToken,
                user?.identityToken,
                requestKidsMode
            );
            const { profiles, profilesRev, profilesLimit, parentalLockOn } = profilesList;
            let regProfileIndex = 0;

            return {
                parentalLockOn: parentalLockOn ?? 0,
                profiles: profiles?.map((el) => {
                    if (!el.kidsMode) {
                        el.avatarIndex = regProfileIndex;
                        regProfileIndex++;
                    }

                    return el;
                }),
                profilesLimit: profilesLimit,
                profileRev: profilesRev,
                kidsMode: requestKidsMode,
                date: new Date().getTime(),
            };
        },
    });
    return useQuery({
        queryKey: getProfileListQueryKey(kidsMode),
        enabled: user !== null,
        staleTime: Infinity,
        queryFn: () => fetchProfileList(user!),
    });
};

export const useUserProfile = (user: UserInfo | null) => {
    const selectedProfile = useSelector((store: IStore) => store.profile.selectedProfile);
    const [validationError, setValidationError] = useState<ProfileValidationError | null>(null);
    const isGuest = user !== null && isGuestUser(user);
    const [isInitializing, setIsInitializing] = useState(() => !isGuest);
    const dispatch = useDispatch();

    const validateProfile = (profileToValidate): boolean => {
        if (!user) {
            setValidationError('Cannot validate profile: No user provided');
            return false;
        }

        if (isGuestUser(user)) {
            return Boolean(profileToValidate?.profileGuid);
        }

        if (!profilesList) {
            setValidationError('Cannot validate profile: profiles not loaded');
            return false;
        }

        const isValidProfile = profilesList.profiles.some((p) => p.profileGuid === profileToValidate.profileGuid);

        if (!isValidProfile) {
            setValidationError("Selected profile is not in the user's profile list");
            return false;
        }

        setValidationError(null);
        return true;
    };

    const {
        isLoading: isProfilesQueryLoading,
        data: profilesList,
        error,
        isError,
    } = useProfileList(isGuest ? null : user);

    useEffect(() => {
        if (profilesList) {
            dispatch(loadProfileDone(profilesList));
        }
    }, [profilesList, dispatch]);

    useEffect(() => {
        if (isError) {
            setIsInitializing(false);
        }
    }, [isError]);

    const { mutate: setUserProfile, isPending } = useMutation({
        mutationFn: async (profile: any) => {
            const result = await switchProfileMutationFn(profile);
            return result;
        },
        onSuccess: ({ result, profile: selectedProfile }) => {
            const { identityToken, showWelcomeScreen, liveLikeAccessToken = null, liveLikeProfileId = null } = result;
            const currentStoredUserData = getUserInfo();
            set(STORE_NAME.USER, { ...currentStoredUserData, identityToken });

            const shouldShowWelcomeScreen = selectedProfile.kidsMode ? true : showWelcomeScreen;

            const updatedProfile = {
                ...selectedProfile,
                date: Date.now(),
                showWelcomeScreen: shouldShowWelcomeScreen,
                liveLikeAccessToken,
                liveLikeProfileId,
            };
            set(STORE_NAME.SELECTED_PROFILE, updatedProfile);
            dispatch(profileInitialize({ list: profilesList, selected: updatedProfile }));

            setIsInitializing(false);
        },
    });

    useEffect(() => {
        if (user === null) {
            return;
        }

        if (isGuest) {
            onGuestUserSession();
            setIsInitializing(false);
            return;
        }

        if (isGuestBrowseProfile(get(STORE_NAME.SELECTED_PROFILE))) {
            onSignedInUserSession(user, { clearProfile: true });
            setIsInitializing(false);
            return;
        }

        if (isProfilesQueryLoading) {
            return;
        }

        if (isError || !profilesList) {
            setIsInitializing(false);
            return;
        }

        if (selectedProfile !== undefined && validateProfile(selectedProfile)) {
            setIsInitializing(false);
            return;
        }

        const localSelectedProfile = get(STORE_NAME.SELECTED_PROFILE);
        if (localSelectedProfile === null) {
            setIsInitializing(false);
            return;
        }

        if (profilesList && validateProfile(localSelectedProfile)) {
            setUserProfile(localSelectedProfile);
        } else {
            setIsInitializing(false);
        }
    }, [user, isGuest, profilesList, selectedProfile, isProfilesQueryLoading, isError, error]);

    return {
        selectedProfile,
        profilesList,
        isProfilesLoading: isGuest ? false : isProfilesQueryLoading || isPending,
        setUserProfile,
        isInitializing: isGuest ? false : isInitializing,
    };
};
